import { Box, Typography, useTheme } from '@mui/material'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useOrder } from 'hooks/useOrders'
import Input from 'components/Input/Input'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { format } from 'date-fns'
import { useLocale } from 'providers/LocaleProvider'
import { OrderPart } from 'types/api/order'

const part = {
  labels: [],
  placeId: '',
  id: '',
  visibleId: '',
  status: '',
  storeId: '',
  deliveryMethod: {
    notes: [],
    service: {
      type: '',
      deliverySlot: {
        startTime: '',
        endTime: '',
        deliveryDate: '',
      },
      address: {
        street: '',
        building: '',
        apartment: '',
        postcode: '',
        postalArea: '',
        city: '',
        state: '',
        additionalInformation: '',
        latitude: '',
        longitude: '',
      },
      pickupLocation: {
        name: '',
        description: '',
        street: '',
        postalCode: '',
        address: '',
      },
    },
  },
}

export const OrderFulfilment = () => {
  const { t } = useTranslation()
  const { selectedLocale } = useLocale()
  const { orderId, partId } = useParams()
  const { isLoading, error, order } = useOrder(orderId as string)
  const partInfo =
    order && order.parts[0].id
      ? order.parts.filter((part: OrderPart) => part.id === partId)[0]
      : part
  const deliveryMethod = partInfo?.deliveryMethod
  const theme = useTheme()

  const initValues = {
    type: t(`fulfilmentStatus.${deliveryMethod.service.type}`),
    slot:
      `${format(
        new Date(deliveryMethod.service.deliverySlot.deliveryDate.slice(0, 10)),
        'P',
        {
          locale: selectedLocale,
        }
      )} ` +
      `${deliveryMethod.service.deliverySlot.startTime}-${deliveryMethod.service.deliverySlot.endTime}`,
    street:
      deliveryMethod.service.type === 'home-delivery'
        ? deliveryMethod.service.address.street
        : '',
    building:
      deliveryMethod.service.type === 'home-delivery'
        ? deliveryMethod.service.address.building
        : '',
    apartment:
      deliveryMethod.service.type === 'home-delivery'
        ? deliveryMethod.service.address.apartment
        : '',
    postcode:
      deliveryMethod.service.type === 'home-delivery'
        ? deliveryMethod.service.address.postcode
        : '',
    postalArea:
      deliveryMethod.service.type === 'home-delivery'
        ? deliveryMethod.service.address.postalArea
        : '',
    city:
      deliveryMethod.service.type === 'home-delivery'
        ? deliveryMethod.service.address.city
        : '',
    state:
      deliveryMethod.service.type === 'home-delivery'
        ? deliveryMethod.service.address.state
        : '',
    latitude:
      deliveryMethod.service.type === 'home-delivery'
        ? deliveryMethod.service.address.latitude
        : '',
    longitude:
      deliveryMethod.service.type === 'home-delivery'
        ? deliveryMethod.service.address.longitude
        : '',
    additionalInformation:
      deliveryMethod.notes.length > 0 ? deliveryMethod.notes[0].message : '',
    collectionPointName:
      deliveryMethod.service.type === 'pickup'
        ? deliveryMethod.service.pickupLocation.name
        : '',
  }

  if (isLoading || !order) return <LoadingIndicator />
  if (error) return <h1>Error</h1>
  return (
    <Formik initialValues={initValues} onSubmit={() => undefined}>
      <Form>
        <Box display='flex' flexDirection='column'>
          {deliveryMethod && (
            <Box
              display='flex'
              flexDirection='column'
              justifyContent='space-around'
              mt={theme.spacing(2)}>
              <Typography
                variant='body2'
                sx={{
                  marginBottom: theme.spacing(4),
                  marginTop: 0,
                }}>
                {t('fulfilment')}
              </Typography>
              <Input
                disabled
                marginBottom={theme.spacing(4)}
                label={t('orderInfo.type')}
                name='type'
                type='text'
                variant='filled'
                fullWidth
              />
              <Input
                disabled
                marginBottom={theme.spacing(4)}
                label={t('orderInfo.slot')}
                name='slot'
                type='text'
                variant='filled'
                width='100%'
                fullWidth
              />
              {deliveryMethod.service.type === 'home-delivery' && (
                <div>
                  <Typography
                    variant='body2'
                    sx={{
                      marginBottom: theme.spacing(4),
                      marginTop: 0,
                    }}>
                    {t('orderInfo.deliveryAddress')}
                  </Typography>
                  <Input
                    disabled
                    marginBottom={theme.spacing(4)}
                    label={t('street')}
                    name='street'
                    type='text'
                    variant='filled'
                    fullWidth
                  />
                  <Box display='flex' justifyContent='flex-start' width='100%'>
                    <Input
                      disabled
                      marginBottom={theme.spacing(4)}
                      label={t('building')}
                      name='building'
                      type='text'
                      variant='filled'
                      fullWidth
                      width='30%'
                    />
                    <Input
                      disabled
                      marginBottom={theme.spacing(4)}
                      label={t('orderInfo.apartment')}
                      name='apartment'
                      type='text'
                      variant='filled'
                      fullWidth
                      width='70%'
                    />
                  </Box>
                  <Box display='flex' justifyContent='flex-start' width='100%'>
                    <Input
                      disabled
                      marginBottom={theme.spacing(4)}
                      label={t('orderInfo.postcode')}
                      name='postcode'
                      type='text'
                      variant='filled'
                      fullWidth
                      width='30%'
                    />
                    <Input
                      disabled
                      marginBottom={theme.spacing(4)}
                      label={t('orderInfo.postalArea')}
                      name='postalArea'
                      type='text'
                      variant='filled'
                      fullWidth
                      width='70%'
                    />
                  </Box>
                  <Input
                    disabled
                    marginBottom={theme.spacing(4)}
                    label={t('city')}
                    name='city'
                    type='text'
                    variant='filled'
                    fullWidth
                  />
                  <Input
                    disabled
                    marginBottom={theme.spacing(4)}
                    label={t('orderInfo.state')}
                    name='state'
                    type='text'
                    variant='filled'
                    fullWidth
                  />
                  <Box display='flex' justifyContent='flex-start' width='100%'>
                    <Input
                      disabled
                      marginBottom={theme.spacing(4)}
                      label={t('latitude')}
                      name='latitude'
                      type='text'
                      variant='filled'
                      fullWidth
                      width='30%'
                    />
                    <Input
                      disabled
                      marginBottom={theme.spacing(4)}
                      label={t('longitude')}
                      name='longitude'
                      type='text'
                      variant='filled'
                      fullWidth
                      width='30%'
                    />
                  </Box>
                  <Typography
                    variant='body2'
                    sx={{
                      marginBottom: theme.spacing(4),
                      marginTop: 0,
                    }}>
                    {t('orderInfo.additionalInformation')}
                  </Typography>
                  <Input
                    disabled
                    marginBottom={theme.spacing(4)}
                    label={t('orderInfo.message')}
                    name='additionalInformation'
                    type='text'
                    variant='filled'
                    fullWidth
                  />
                </div>
              )}
              {deliveryMethod.service.type === 'pickup' && (
                <div>
                  <Typography
                    variant='body2'
                    sx={{
                      marginBottom: theme.spacing(4),
                      marginTop: 0,
                    }}>
                    {t('orderInfo.collectionPoint')}
                  </Typography>
                  <Input
                    disabled
                    marginBottom={theme.spacing(4)}
                    label={t('orderInfo.collectionPointName')}
                    name='collectionPointName'
                    type='text'
                    variant='filled'
                    fullWidth
                  />
                </div>
              )}
            </Box>
          )}
        </Box>
      </Form>
    </Formik>
  )
}
